/**
 * Geocoding API Client
 * Calls backend geocoding service (which caches results)
 */

const API_BASE = `${import.meta.env.VITE_API_URL || '/meos/api'}/geocoding`;

export interface GeocodeResult {
  location: string;
  address?: string;
  cached: boolean;
}

/**
 * Reverse geocode a single coordinate pair
 */
export const reverseGeocode = async (lat: number, lng: number): Promise<GeocodeResult> => {
  try {
    const response = await fetch(`${API_BASE}/reverse`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('ma_jwt') || ''}`,
      },
      body: JSON.stringify({ lat, lng }),
    });

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.warn('Reverse geocoding failed:', error);
    // Return coordinates as fallback
    return {
      location: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
      cached: false,
    };
  }
};

/**
 * Batch reverse geocode multiple coordinates
 * Backend will only geocode maxNew new locations to respect rate limits
 */
export const reverseGeocodeBatch = async (
  coordinates: Array<{ lat: number; lng: number }>,
  maxNew: number = 3
): Promise<Record<string, GeocodeResult>> => {
  try {
    const response = await fetch(`${API_BASE}/batch`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token') || localStorage.getItem('ma_jwt') || ''}`,
      },
      body: JSON.stringify({ coordinates, maxNew }),
    });

    if (!response.ok) {
      throw new Error(`Batch geocoding failed: ${response.status}`);
    }

    const data = await response.json();
    return data.data;
  } catch (error) {
    console.warn('Batch geocoding failed:', error);
    // Return coordinates as fallback for all
    const results: Record<string, GeocodeResult> = {};
    coordinates.forEach((coord) => {
      const key = `${Number(coord.lat.toFixed(4))},${Number(coord.lng.toFixed(4))}`;
      results[key] = {
        location: `${coord.lat.toFixed(4)}, ${coord.lng.toFixed(4)}`,
        cached: false,
      };
    });
    return results;
  }
};
